// components/DeleteUserModal.tsx
import React, { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { AlertTriangle, Loader2, Trash2 } from '@/components/ui/lucide-memo';
import { toast } from 'sonner';
import type { UserDeletionOptions } from '#types/types';

interface DeleteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userEmail: string;
  onSubmit: (userId: string, options: UserDeletionOptions) => Promise<void>;
}

const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  isOpen,
  onClose,
  userId,
  userEmail,
  onSubmit,
}) => {
  const [options, setOptions] = useState<UserDeletionOptions>({
    deleteUserProjects: true,
    deleteGenerationLogs: true,
    deleteClerkUser: true,
  });
  const [confirmationInput, setConfirmationInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isConfirmationValid = confirmationInput === userEmail;

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setOptions((previous) => ({ ...previous, [name]: checked }));
  };

  const handleConfirmationChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setConfirmationInput(event.target.value);
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isConfirmationValid) {
      setError('Confirmation text does not match the user email.');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await onSubmit(userId, options);
      toast.success(`User "${userEmail}" has been submitted for deletion.`);
    } catch (submitError: unknown) {
      const errorMessage = submitError instanceof Error
        ? submitError.message
        : 'Failed to submit deletion request.';
      setError(errorMessage);
      toast.error(`Deletion failed: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing in this file, inherited by the merge gate (it judges whole files); changing it alters behaviour outside this merge
    setOptions({
      deleteUserProjects: true,
      deleteGenerationLogs: true,
      deleteClerkUser: true,
    });
    setConfirmationInput('');
    setError(null);
    setIsSubmitting(false);
  }, [isOpen, userId]);

  if (!isOpen) return null;

  const checkboxClass =
    'mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-[#2A2A2A] bg-[#141414] accent-[#FF7369] disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#141414]/90 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-[24px] border border-white/[0.06] bg-[#212121] text-white shadow-2xl">
        <header className="border-b border-white/[0.055] px-5 py-4">
          <div className="flex items-center gap-2">
            <AlertTriangle size={17} className="shrink-0 text-[#FF7369]" />
            <h2 className="text-lg font-semibold text-white/90">Delete user</h2>
          </div>
          <p className="mt-2 text-sm leading-5 text-white/50">
            This permanently deletes <span className="font-medium text-white/75">{userEmail}</span> and the selected related data. This action cannot be undone.
          </p>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="px-5">
            <section className="divide-y divide-white/[0.055]">
              <label htmlFor="delete-clerk-user" className="flex cursor-pointer items-start gap-3 py-3">
                <input
                  type="checkbox"
                  id="delete-clerk-user"
                  name="deleteClerkUser"
                  checked={options.deleteClerkUser}
                  onChange={handleCheckboxChange}
                  className={checkboxClass}
                  disabled={isSubmitting}
                />
                <span>
                  <span className="block text-sm font-medium text-white/75">Delete Clerk user account</span>
                  <span className="mt-0.5 block text-xs text-white/35">Recommended for a complete account deletion.</span>
                </span>
              </label>

              <label htmlFor="delete-user-projects" className="flex cursor-pointer items-start gap-3 py-3">
                <input
                  type="checkbox"
                  id="delete-user-projects"
                  name="deleteUserProjects"
                  checked={options.deleteUserProjects}
                  onChange={handleCheckboxChange}
                  className={checkboxClass}
                  disabled={isSubmitting}
                />
                <span>
                  <span className="block text-sm font-medium text-white/75">Delete user projects</span>
                  <span className="mt-0.5 block text-xs text-white/35">Remove projects owned by this user.</span>
                </span>
              </label>

              <label htmlFor="delete-generation-logs" className="flex cursor-pointer items-start gap-3 py-3">
                <input
                  type="checkbox"
                  id="delete-generation-logs"
                  name="deleteGenerationLogs"
                  checked={options.deleteGenerationLogs}
                  onChange={handleCheckboxChange}
                  className={checkboxClass}
                  disabled={isSubmitting}
                />
                <span>
                  <span className="block text-sm font-medium text-white/75">Delete generation history</span>
                  <span className="mt-0.5 block text-xs text-white/35">Remove generation logs associated with this user.</span>
                </span>
              </label>
            </section>

            <section className="border-t border-white/[0.055] py-4">
              <label htmlFor="user-email-confirm" className="block text-xs font-medium text-white/55">
                Type <span className="font-mono text-white/75">{userEmail}</span> to confirm
              </label>
              <input
                type="text"
                id="user-email-confirm"
                value={confirmationInput}
                onChange={handleConfirmationChange}
                required
                className={`mt-2 h-9 w-full rounded-md border bg-[#141414] px-3 text-sm text-white/90 outline-none transition-colors placeholder:text-white/25 ${
                  isConfirmationValid
                    ? 'border-[#4DAB9A]/60 focus:border-[#4DAB9A]'
                    : confirmationInput
                      ? 'border-[#FF7369]/60 focus:border-[#FF7369]'
                      : 'border-[#2A2A2A] focus:border-white/20'
                }`}
                disabled={isSubmitting}
                autoComplete="off"
              />
              {error && <p className="mt-2 text-sm text-[#FF7369]">{error}</p>}
            </section>
          </div>

          <footer className="flex justify-end gap-2 border-t border-white/[0.055] px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="inline-flex h-8 items-center rounded-md px-3 text-sm text-white/60 transition-colors hover:bg-white/[0.06] hover:text-white/85 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isConfirmationValid || isSubmitting}
              className="inline-flex h-8 min-w-28 items-center justify-center gap-1.5 rounded-md border border-[#FF7369]/25 bg-[#FF7369]/10 px-3 text-sm font-medium text-[#FF7369] transition-colors hover:bg-[#FF7369]/15 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isSubmitting ? <Loader2 size={15} className="animate-spin" /> : <><Trash2 size={14} /> Delete user</>}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default DeleteUserModal;
